import {
  Box,
  Text,
  Dialog,
  Button,
  Checkbox,
  Divider,
  Stack,
  Badge,
  Notice,
} from 'braid-design-system';
import queryString from 'query-string';
import React, { Fragment, useReducer } from 'react';
import { useNavigate } from 'react-router-dom';

import Routes from 'src/config/routes';
import type {
  ClientListingForListingsTab,
  ListingProductForListingsTab,
} from 'src/types/graphql-derived';
import { decimalFormatPriceBand } from 'src/utils/investmentBands';

interface BoostDialogProps {
  isOpen: boolean;
  listing: ClientListingForListingsTab;
  searchBoostProduct: ListingProductForListingsTab;
  emailBoostProduct: ListingProductForListingsTab;
  onClose: () => void;
}

export const BoostDialog = ({
  isOpen,
  listing,
  emailBoostProduct,
  searchBoostProduct,
  onClose,
}: BoostDialogProps) => {
  const navigate = useNavigate();
  const [state, dispatch] = useReducer(reducer, initialState);

  const handleClose = () => {
    dispatch({ type: 'close-dialog' });
    onClose();
  };

  const totalCost = () => {
    let total = 0;
    if (state.isEmailBoostSelected) {
      total += emailBoostProduct.cost;
    }
    if (state.isSearchBoostSelected) {
      total += searchBoostProduct.cost;
    }
    return total;
  };

  const selectedProductTokens = () => {
    const result = [];
    if (state.isEmailBoostSelected) {
      result.push(emailBoostProduct.productToken);
    }
    if (state.isSearchBoostSelected) {
      result.push(searchBoostProduct.productToken);
    }
    return result;
  };

  return (
    <Dialog
      id="select-boost-packages"
      title="Select boost packages"
      description={
        <Text tone="secondary" size="small">
          Ready to accelerate your sale? Choose how to boost your ad&apos;s
          visibility to buyers.
        </Text>
      }
      width="small"
      open={isOpen}
      onClose={handleClose}
    >
      <Stack space="medium">
        <Box display="flex" justifyContent="spaceBetween" alignItems="center">
          <Checkbox
            id="website-search-boost"
            onChange={() => dispatch({ type: 'toggle-search-boost' })}
            checked={listing.isPremium || state.isSearchBoostSelected}
            disabled={listing.isPremium}
            label="Website Search Boost"
            description="Stand out where it matters – at the top of search results and on our homepage for buyers in your location."
            tone="neutral"
            badge={
              listing.isPremium ? (
                <Badge
                  tone="positive"
                  weight="strong"
                  data={{ testid: 'boost-dialog-search-boost-active-badge' }}
                >
                  Active
                </Badge>
              ) : undefined
            }
          />

          {!listing.isPremium && (
            <Box
              flexShrink={0}
              paddingLeft={{ mobile: 'xsmall', tablet: 'large' }}
            >
              <Text
                weight={state.isSearchBoostSelected ? 'strong' : 'regular'}
                data={{ testid: 'boost-dialog-search-boost-price' }}
              >
                {decimalFormatPriceBand(searchBoostProduct.cost / 100)}
              </Text>
            </Box>
          )}
        </Box>

        <Box display="flex" justifyContent="spaceBetween" alignItems="center">
          <Checkbox
            id="email-boost"
            onChange={() => dispatch({ type: 'toggle-email-boost' })}
            checked={
              listing.isListingEmailBoosted || state.isEmailBoostSelected
            }
            disabled={listing.isListingEmailBoosted}
            label="Email Boost"
            tone="neutral"
            description="Reach motivated buyers directly – get featured at the top of our daily recommendation emails."
            badge={
              listing.isListingEmailBoosted ? (
                <Badge
                  tone="positive"
                  weight="strong"
                  data={{ testid: 'boost-dialog-email-boost-active-badge' }}
                >
                  Active
                </Badge>
              ) : undefined
            }
          />

          {!listing.isListingEmailBoosted && (
            <Box
              flexShrink={0}
              paddingLeft={{ mobile: 'xsmall', tablet: 'large' }}
            >
              <Text
                weight={state.isEmailBoostSelected ? 'strong' : 'regular'}
                data={{ testid: 'boost-dialog-email-boost-price' }}
              >
                {decimalFormatPriceBand(emailBoostProduct.cost / 100)}
              </Text>
            </Box>
          )}
        </Box>

        {(state.isSearchBoostSelected || state.isEmailBoostSelected) && (
          <Fragment>
            <Divider />

            <Box
              display="flex"
              justifyContent="spaceBetween"
              alignItems="center"
              paddingY="xxsmall"
            >
              <Text size="large" weight="medium">
                Total:
              </Text>
              <Text
                size="large"
                weight="medium"
                data={{ testid: 'boost-dialog-total-price' }}
              >
                {decimalFormatPriceBand(totalCost() / 100)} + GST
              </Text>
            </Box>
          </Fragment>
        )}

        {state.hasSubmissionError && (
          <Box paddingY="small">
            <Notice
              tone="critical"
              data={{ testid: 'boost-dialog-error-feedback' }}
            >
              <Text>Please select a product to proceed.</Text>
            </Notice>
          </Box>
        )}

        <Box>
          <Button
            onClick={() => {
              const productTokens = selectedProductTokens();

              if (productTokens.length === 0) {
                dispatch({ type: 'invalid-submission' });
                return;
              }

              navigate(
                queryString.stringifyUrl({
                  url: Routes.PAYMENT,
                  query: {
                    productToken: productTokens,
                    listingId: listing.id,
                  },
                }),
              );
            }}
          >
            Continue to payment
          </Button>
        </Box>
      </Stack>
    </Dialog>
  );
};

interface State {
  hasSubmissionError: boolean;
  isEmailBoostSelected: boolean;
  isSearchBoostSelected: boolean;
}

type Action =
  | { type: 'toggle-email-boost' }
  | { type: 'toggle-search-boost' }
  | { type: 'invalid-submission' }
  | { type: 'close-dialog' };

const initialState: State = {
  hasSubmissionError: false,
  isEmailBoostSelected: false,
  isSearchBoostSelected: false,
};

const reducer = (state: State, action: Action): typeof state => {
  switch (action.type) {
    case 'toggle-email-boost':
      return {
        ...state,
        hasSubmissionError: false,
        isEmailBoostSelected: !state.isEmailBoostSelected,
      };
    case 'toggle-search-boost':
      return {
        ...state,
        hasSubmissionError: false,
        isSearchBoostSelected: !state.isSearchBoostSelected,
      };
    case 'invalid-submission':
      return {
        ...state,
        hasSubmissionError: true,
      };
    case 'close-dialog':
      return { ...initialState };
  }
};
